import { createContext, useContext, useEffect } from 'react';

import { hasSeenTour } from '@/features/tour/storage.js';
import type { TourId } from '@/features/tour/types.js';

export interface ActiveTour {
  readonly id: TourId;
  /** The step showing, from 0. */
  readonly index: number;
}

export interface TourState {
  /** The tour on screen, or null. Only one at a time. */
  readonly active: ActiveTour | null;
  /** Undefined until the account has loaded; no tour runs before then. */
  readonly customerId: string | undefined;
  /** Moves on every time a tour is marked seen, so a tour waiting its turn looks again. */
  readonly seenVersion: number;
  /** Opens a tour at its first step — a "How it works" button, or an automatic first visit. */
  start: (id: TourId) => void;
  go: (index: number) => void;
  /** Closes the tour on screen — Skip, Escape, Done alike — and remembers it was seen. */
  finish: () => void;
}

export const TourContext = createContext<TourState | null>(null);

export function useTours(): TourState {
  const value = useContext(TourContext);
  if (!value) throw new Error('useTours must be used inside TourProvider');
  return value;
}

/**
 * Opens a tour by itself on the customer's first visit (MYP-83) — once `enabled`, when the screen
 * has what the tour points at.
 *
 * Tours take turns: nothing opens over another tour, and every tour but `welcome` waits until the
 * welcome has been seen or skipped. When one closes, the others look again.
 */
export function useAutoTour(id: TourId, enabled = true): void {
  const { active, customerId, seenVersion, start } = useTours();

  useEffect(() => {
    if (!enabled || !customerId || active) return;
    if (id !== 'welcome' && !hasSeenTour('welcome', customerId)) return;
    if (hasSeenTour(id, customerId)) return;
    start(id);
  }, [enabled, customerId, active, id, seenVersion, start]);
}

/** The step of `id` on screen, or null when that tour is not the one running. */
export function useTourStep(id: TourId): number | null {
  const { active } = useTours();
  return active?.id === id ? active.index : null;
}
